import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNativeChrome } from '../hooks/useNativeChrome';
import { useAuth } from '../context/AuthContext';
import { BrandMark } from '../components/ui/bits';
import {
  AuthHeader,
  AuthPage,
  AuthTitle,
  ErrorNote,
  PrimaryButton,
  TextField,
  authMessage
} from '../components/auth/parts';
import { Check, Mail } from '../components/ui/icons';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function ResetPassword() {
  useNativeChrome();
  const navigate = useNavigate();
  const { sendPasswordReset } = useAuth();
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!EMAIL_RE.test(email.trim())) {
      setError('That email doesn’t look right.');
      return;
    }
    setBusy(true);
    try {
      await sendPasswordReset(email.trim());
      setSent(true);
    } catch (err) {
      setError(authMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthPage>
      <AuthHeader backLabel="Log in" onBack={() => navigate('/login')} />

      <div className="flex flex-1 flex-col">
        <BrandMark size={44} className="mt-2" />

        {sent ? (
          <div className="mt-4">
            <span className="grid h-12 w-12 place-items-center rounded-2xl bg-accent-wash text-accent-ink">
              <Check width={24} height={24} strokeWidth={3} />
            </span>
            <div className="mt-4">
              <AuthTitle
                title="Check your inbox"
                subtitle={
                  <>
                    We sent a reset link to{' '}
                    <span className="font-semibold text-ink">{email.trim()}</span>. Follow it to
                    choose a new password.
                  </>
                }
              />
            </div>
            <div className="mt-6">
              <PrimaryButton onClick={() => navigate('/login')}>Back to log in</PrimaryButton>
            </div>
          </div>
        ) : (
          <>
            <div className="mt-4">
              <AuthTitle
                title="Reset your password"
                subtitle="Enter your email and we’ll send you a link to set a new one."
              />
            </div>

            <form onSubmit={onSubmit} className="mt-6 flex flex-col gap-4">
              <TextField
                label="Email"
                type="email"
                inputMode="email"
                value={email}
                onChange={setEmail}
                placeholder="you@example.com"
                autoComplete="email"
                autoFocus
                prefix={<Mail width={18} height={18} />}
              />

              {error && <ErrorNote>{error}</ErrorNote>}

              <div className="mt-1">
                <PrimaryButton type="submit" busy={busy} disabled={busy}>
                  Send reset link
                </PrimaryButton>
              </div>
            </form>

            <div className="flex-1" />

            <p className="pb-2 pt-5 text-center text-[14px] font-medium text-ink2">
              Remembered it?{' '}
              <button type="button" onClick={() => navigate('/login')} className="font-bold text-accent-ink">
                Log in
              </button>
            </p>
          </>
        )}
      </div>
    </AuthPage>
  );
}
